import type { Block, BlockType, PanelStyle } from '@/pages/Panels/config-types'

import { api } from '@/lib/api'

/** A panel is an ordered list of typed blocks + style-as-data (see config-types). */
export interface Panel {
  id: string
  slug: string
  name: string
  config: { blocks: Block[]; style: PanelStyle }
  token: string
  project_scope: string[]
  expose_in_router: boolean
  enabled: boolean
  render_identity: 'app' | 'viewer'
  placement: 'content' | 'sidebar'
  url_template: string
  sr_url: string
  block_types: string[] // derived server-side from config.blocks[].type
}

// Shared presentation metadata — one label per block type, so the list tags, the
// add-block menu and the block inspector stay in lockstep. Neutral: no colour per type.
export const PANEL_TYPE_META: Record<BlockType, { label: string }> = {
  issue_fields: { label: 'Issue fields' },
  bamboo_profile: { label: 'BambooHR profile' },
  jql_table: { label: 'JQL table' },
  progress_checklist: { label: 'Progress checklist' },
  custom_html: { label: 'Custom HTML' },
  slack_dm_button: { label: 'Slack DM button' },
  fin_renewal_button: { label: 'FIN renewal button' },
  create_issue_button: { label: 'Create issue button' },
  heading: { label: 'Heading' },
  divider: { label: 'Divider' },
}

// The admin PUT replaces the whole record, so partial edits (e.g. an enabled
// toggle in the list) must resend every mutable field. This picks exactly those.
export const toPanelBody = (p: Panel): Partial<Panel> => ({
  name: p.name,
  config: p.config,
  project_scope: p.project_scope,
  expose_in_router: p.expose_in_router,
  enabled: p.enabled,
  render_identity: p.render_identity,
  placement: p.placement,
})

export const listPanels = () => api<Panel[]>('/panels')

export const getPanel = (id: string) => api<Panel>(`/panels/${id}`)

export const createPanel = (body: Partial<Panel>) =>
  api<Panel>('/panels', { method: 'POST', body: JSON.stringify(body) })

export const updatePanel = (id: string, body: Partial<Panel>) =>
  api<Panel>(`/panels/${id}`, { method: 'PUT', body: JSON.stringify(body) })

export const deletePanel = (id: string) => api<void>(`/panels/${id}`, { method: 'DELETE' })

export const rotateToken = (id: string) =>
  api<Panel>(`/panels/${id}/rotate-token`, { method: 'POST' })

export const previewPanel = (id: string, issueKey: string) =>
  api<{ html: string }>(`/panels/${id}/preview`, {
    method: 'POST',
    body: JSON.stringify({ issue_key: issueKey }),
  })

// Last test issue key used to preview a panel — a per-browser convenience, so the list
// can show a live preview without asking again. Storage may be unavailable: never throw.
const keyFor = (id: string) => `vulpio.panels.testIssueKey.${id}`

export function getTestIssueKey(id: string): string {
  try {
    return localStorage.getItem(keyFor(id)) ?? ''
  } catch {
    return ''
  }
}

export function setTestIssueKey(id: string, key: string) {
  try {
    if (key) localStorage.setItem(keyFor(id), key)
    else localStorage.removeItem(keyFor(id))
  } catch {
    /* storage blocked — the preview just won't be remembered */
  }
}
